import { styled } from "goober";
import { useMemo } from "react";
import { connectorDirectory } from "../lib/connectorDirectory";
import type { Connector } from "../lib/connector";
import type { ParsedStep } from "../lib/parseCommitData";
import { playClick } from "../lib/sounds";
import { navigate, routes } from "../lib/urls";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { FacePile } from "./FacePile";
import {
  StatusBreakdownChart,
  type StatusSegment,
} from "./StatusBreakdownChart";
import { Tooltip } from "./Tooltip";

type Props = {
  connectors: ReadonlyArray<Connector>;
  steps: ReadonlyArray<ParsedStep>;
  onStepsHover?: (canonicalIds: ReadonlyArray<string> | null) => void;
};

type ConnectorHealth = "success" | "failure" | "pending" | "untested";

const CONNECTOR_STATUS: Array<{
  key: ConnectorHealth;
  color: string;
  label: string;
}> = [
  { key: "success", color: "var(--green-600)", label: "connected" },
  { key: "failure", color: "var(--red-600)", label: "failing" },
  { key: "pending", color: "var(--orange-600)", label: "draft" },
  { key: "untested", color: "var(--gray-400)", label: "untested" },
];

const Outer = styled("div")`
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export function WorkflowConnectorsPanel({
  connectors,
  steps,
  onStepsHover,
}: Props) {
  const missingSlots = useMemo(() => {
    const bySlug = new Map<string, { slug: string; appType: string }>();
    for (const s of steps) {
      for (const ca of s.connectedApps) {
        if (!ca.connectedApp && !bySlug.has(ca.slug))
          bySlug.set(ca.slug, { slug: ca.slug, appType: ca.appType });
      }
    }
    return [...bySlug.values()];
  }, [steps]);

  const segments: StatusSegment[] = useMemo(() => {
    const buckets: Record<ConnectorHealth, string[]> = {
      success: [],
      failure: [],
      pending: [],
      untested: [],
    };
    for (const a of connectors) {
      const status = a.lastTestStatus;
      if (
        status === "success" ||
        status === "failure" ||
        status === "pending"
      ) {
        buckets[status].push(a.name);
      } else {
        buckets.untested.push(a.name);
      }
    }
    return CONNECTOR_STATUS.map((d) => ({
      key: d.key,
      count: buckets[d.key].length,
      color: d.color,
      label: d.label,
      items: buckets[d.key],
    }));
  }, [connectors]);

  const appIdsByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of connectors) m.set(a.name, a.id);
    return m;
  }, [connectors]);

  const handleSegmentHover = (seg: StatusSegment | null) => {
    if (!seg) {
      onStepsHover?.(null);
      return;
    }
    const connectorIds = new Set(
      (seg.items ?? [])
        .map((name) => appIdsByName.get(name))
        .filter((id): id is string => id != null)
    );
    const ids = steps
      .filter((s) =>
        s.connectedApps.some(
          (ca) => ca.connectedAppId && connectorIds.has(ca.connectedAppId)
        )
      )
      .map((s) => s.canonicalId);
    onStepsHover?.(ids);
  };

  const handleConnectorHover = (connectorId: string | null) => {
    if (!connectorId) {
      onStepsHover?.(null);
      return;
    }
    const ids = steps
      .filter((s) =>
        s.connectedApps.some((ca) => ca.connectedAppId === connectorId)
      )
      .map((s) => s.canonicalId);
    onStepsHover?.(ids);
  };

  const connectedItems = connectors.map((a) => ({
    node: (
      <Tooltip content={a.name}>
        <div
          style={{ cursor: "pointer" }}
          onMouseEnter={() => handleConnectorHover(a.id)}
          onMouseLeave={() => handleConnectorHover(null)}
          onMouseDown={(e) => {
            e.stopPropagation();
            playClick();
            navigate(routes.modal.connector, { id: a.id });
          }}
        >
          <ConnectorLogoContainer size={24} outline={false} outlineWidth={2}>
            <ConnectorLogo app={a} size={12} />
          </ConnectorLogoContainer>
        </div>
      </Tooltip>
    ),
    id: a.id,
    label: a.name,
    bare: true,
  }));

  const missingItems = missingSlots.map(({ slug, appType }) => {
    const connectorDef = connectorDirectory.find(
      (a) => a.identifier.toLowerCase() === appType.toLowerCase()
    );
    const name = slug || (connectorDef?.name ?? appType);
    const identifier = connectorDef?.identifier ?? appType;
    const stepsNeeding = steps.filter((s) =>
      s.connectedApps.some((ca) => !ca.connectedApp && ca.slug === slug)
    );
    return {
      node: (
        <Tooltip content={`Connect ${name}`}>
          <div
            style={{ cursor: "pointer" }}
            onMouseEnter={() =>
              onStepsHover?.(stepsNeeding.map((s) => s.canonicalId))
            }
            onMouseLeave={() => onStepsHover?.(null)}
            onMouseDown={(e) => {
              e.stopPropagation();
              playClick();
              const first = stepsNeeding[0];
              if (first)
                navigate(routes.space.workflow, {
                  step: first.canonicalId,
                  file: "",
                });
            }}
          >
            <ConnectorLogoContainer size={24} outline={false} outlineWidth={2}>
              <ConnectorLogo template={{ name, identifier }} size={12} faded />
            </ConnectorLogoContainer>
          </div>
        </Tooltip>
      ),
      id: `missing-${slug}`,
      label: `Connect ${name}`,
      bare: true,
    };
  });

  const pileItems = [...connectedItems, ...missingItems];

  const appStack =
    pileItems.length > 0 ? (
      <FacePile itemSize={24} outlineWidth={2} items={pileItems} cutout />
    ) : null;

  return (
    <Outer>
      <StatusBreakdownChart
        title="Connectors status"
        total={connectors.length}
        segments={segments}
        onSegmentHover={handleSegmentHover}
        accessory={appStack}
      />
    </Outer>
  );
}
